import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Chart } from "chart.js";
import { IonicPage, NavParams } from "ionic-angular";
import * as _ from "lodash";
import moment from "moment";
import { forkJoin } from "rxjs/observable/forkJoin";
import { take } from "rxjs/operators";
import CollapsibleEvent from "../../../models/events/collapsible-modal.event";
import Measure from "../../../models/patient-chart/biometrics/measure";
import { ConfigurationService } from "../../../providers/configuration/configuration.service";
import { BiometricsService } from "../../../providers/patient-charts/biometrics.service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";

@IonicPage()
@Component({
  selector: "patient-biometrics",
  templateUrl: "biometrics.html",
})
export class BiometricsPage implements OnInit {
  // page controls
  @ViewChild("lineCanvas") lineCanvas: ElementRef;
  private lineChart: Chart;
  measuresOpened: boolean = true;
  patientChartId: number;

  // form
  biometricsForm: FormGroup;

  maxDataPoints = 30;

  constructor(
    private referralsService: ReferralsServiceProvider,
    private stateData: StateManagementService,
    private biometricsService: BiometricsService,
    private fb: FormBuilder,
    public navParams: NavParams,
    private config: ConfigurationService
  ) {
    this.patientChartId = navParams.data.patientChartId;

    if (
      this.config.getConfig().isLocal ||
      this.config.getConfig().isDev ||
      this.config.getConfig().isQA
    ) {
      this.maxDataPoints = 5;
    }
  }

  // region implement interfaces
  ionViewWillEnter() {
    this.referralsService.ACTIVE_TAB_NAME = "biometrics";
    this.stateData.setSectionId("Biometrics");
  }

  ngOnInit(): void {
    this.biometricsForm = this.fb.group({
      measure: this.fb.control(""),
    });

    this.lineChart = new Chart(this.lineCanvas.nativeElement, {
      type: "line",
      data: {
        labels: [],
        datasets: [
          {
            label: "Weight(lbs)",
            fill: false,
            lineTension: 0.1,
            backgroundColor: "rgba(75,192,192,0.4)",
            borderColor: "rgba(75,192,192,1)",
            borderCapStyle: "butt",
            borderDash: [],
            borderDashOffset: 0.0,
            borderJoinStyle: "miter",
            pointBorderColor: "rgba(75,192,192,1)",
            pointBackgroundColor: "#fff",
            pointBorderWidth: 1,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: "rgba(75,192,192,1)",
            pointHoverBorderColor: "rgba(220,220,220,1)",
            pointHoverBorderWidth: 2,
            pointRadius: 1,
            pointHitRadius: 10,
            data: [],
            spanGaps: false,
          },
        ],
      },
    });
    this.biometricsForm.controls["measure"].valueChanges.subscribe((x) =>
      this.handleMeasureChange(x)
    );
  }

  initNewDataSet(): any {
    return {
      label: "Weight(lbs)",
      fill: false,
      lineTension: 0.1,
      backgroundColor: "rgba(75,192,192,0.4)",
      borderColor: "rgba(75,192,192,1)",
      borderCapStyle: "butt",
      borderDash: [],
      borderDashOffset: 0.0,
      borderJoinStyle: "miter",
      pointBorderColor: "rgba(75,192,192,1)",
      pointBackgroundColor: "#fff",
      pointBorderWidth: 1,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: "rgba(75,192,192,1)",
      pointHoverBorderColor: "rgba(220,220,220,1)",
      pointHoverBorderWidth: 2,
      pointRadius: 1,
      pointHitRadius: 10,
      data: [],
      spanGaps: false,
    };
  }

  // endregion

  fetchMeasures(value: string) {
    if (value !== "bloodPressure" && value !== "muacScore") {
      this.addSingleMeasure(value);
    } else {
      this.addDoubleMeasure(value);
    }
  }

  addDoubleMeasure(value: string) {
    let measure;
    let measureLabel;
    let measure2;
    let measureLabel2;
    switch (value) {
      case "bloodPressure":
        measure = this.biometricsService.getReadings(
          this.patientChartId,
          "diastolic"
        );
        measure2 = this.biometricsService.getReadings(
          this.patientChartId,
          "systolic"
        );
        measureLabel = "Diastolic";
        measureLabel2 = "Systolic";
        break;
      case "muacScore":
        measure = this.biometricsService.getReadings(
          this.patientChartId,
          "leftArmInches"
        );
        measure2 = this.biometricsService.getReadings(
          this.patientChartId,
          "rightArmInches"
        );
        measureLabel = "Left Arm";
        measureLabel2 = "Right Arm";
        break;
      default:
        console.log(`Invalid measure ${value}`);
        return;
    }

    forkJoin([measure, measure2]).subscribe(
      ([d, s]: [Measure, Measure]) => {
        this.lineChart.data.datasets.push(this.initNewDataSet());
        this.lineChart.data.datasets.push(this.initNewDataSet());
        this.lineChart.data.labels = [];
        let measureDataset = this.lineChart.data.datasets[0];
        let measureDataset2 = this.lineChart.data.datasets[1];
        console.log(
          `Receiving data for measure ${value}: ${d.readings} : ${s.readings}`
        );

        if (d.readings && s.readings) {
          if (d.readings.length > this.maxDataPoints) {
            d.readings = _.takeRight(d.readings, this.maxDataPoints);
          }
          d.readings.forEach((r1) => {
            console.log(`r1: ${r1.value}`);
            measureDataset.data.push(r1.value);
            measureDataset.label = measureLabel;
            this.lineChart.data.labels.push(
              moment(r1.time).format("MM/DD/YYYY HH:mm:ss")
            );
          });

          if (s.readings.length > this.maxDataPoints) {
            s.readings = _.takeRight(s.readings, this.maxDataPoints);
          }
          s.readings.forEach((r2) => {
            console.log(`r2: ${r2.value}`);
            measureDataset2.label = measureLabel2;
            measureDataset2.data.push(r2.value);
          });

          this.lineChart.update();
        }
      },
      (err) =>
        console.log(`Failed to fetch data for measure: ${value} error: ${err}`)
    );
  }

  addSingleMeasure(measure: string): void {
    let measureLabel;
    switch (measure) {
      case "kilograms":
        measureLabel = "Weight(kg)";
        break;
      case "pounds":
        measureLabel = "Weight(lbs)";
        break;
      case "rate":
        measureLabel = "Rate";
        break;
      case "mmseScore":
        measureLabel = "MMSE Score";
        break;
      case "assessorScore":
        measureLabel = "PPS Score";
        break;
      case "karnofskyValue":
        measureLabel = "Karnofsky Score";
        break;
      case "nyhaClass":
        measureLabel = "NYHA Score";
        break;
      case "fastValue":
        measureLabel = "Fast Score";
        break;
      default:
        console.log(`Invalid measure ${measure}`);
        return;
    }
    this.biometricsService
      .getReadings(this.patientChartId, measure)
      .pipe(take(1))
      .subscribe(
        (data) => {
          this.lineChart.data.datasets.push(this.initNewDataSet());
          this.lineChart.data.labels = [];
          let ds = this.lineChart.data.datasets[0];
          ds.label = measureLabel;
          if (data.readings) {
            if (data.readings.length > this.maxDataPoints) {
              data.readings = _.takeRight(data.readings, this.maxDataPoints);
            }
            data.readings.forEach((r) => {
              ds.data.push(r.value);
              this.lineChart.data.labels.push(
                moment(r.time).format("MM/DD/YYYY HH:mm:ss")
              );
            });

            this.lineChart.update();
          }
        },
        (err) =>
          console.log(
            `Failed to fetch data for measure: ${measure} error: ${err}`
          )
      );
  }

  // region html handlers

  /**
   * Handles the update from one of the measure control changes
   * @param value
   */
  handleMeasureChange(value: any) {
    this.lineChart.data.datasets = [];
    this.fetchMeasures(value);
  }

  /**
   * Handles the collapsible header click event
   * @param $event the section clicked
   */
  onCollapseClicked($event: CollapsibleEvent) {
    console.log($event);
    if ($event.controlId === "measure") {
      this.measuresOpened = $event.isOpen;
    }
  }

  // endregion
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/biometrics/biometrics.ts